import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CountableTimeZonePipe } from 'countable@helpers';
import { AiDownloadModel, AiFirmInfo, AiFirmModel, AiInfoModel, ChatResponseModel, ChatsModel, CommonAIResModel, ContextOtherApiModel, ContextOtherApiRes, ContextResModel, GenericResModel, SaveChatMsgModel, SavedChatHistoryModel, SaveExcelModel, SaveOtherApiModel, TbAccountModel, UserModel } from 'countable@model';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AutoDocDataModel } from '../model/docrepo/auto-doc-data.model';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})

export class AiService {

  public attachedFiles: BehaviorSubject<Map<string, File>> = new BehaviorSubject<Map<string, File>>(null);
  public uploadedFiles: BehaviorSubject<File[]> = new BehaviorSubject<File[]>([])
  public currentUUID: string;
  public cachedPromptResponse: Map<string, GenericResModel<CommonAIResModel>> = new Map<string, GenericResModel<CommonAIResModel>>([]);
  public cachedChatResponse: Map<string, ChatResponseModel> = new Map<string, ChatResponseModel>([]);
  public isMinimized: boolean = false;
  public progressType: 'PROMPT' | 'FILE_PROMPT' | 'FREE' | 'NONE' = 'NONE';
  public userInfo: UserModel;

  constructor(private httpClient: HttpClient, private countableTimeZonePipe: CountableTimeZonePipe,private authService: AuthService) {
    this.authService.subjectUser.subscribe({
      next: user => {
        if(user) {
          this.userInfo = user;
        }
      }
    })
  }

  public fetchGptResponse(data: FormData): Observable<any> {
    return new Observable(observer => {
      const url = environment.ai + 'ask-luca/';
      fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + this.userInfo?.token,
        },
        body: data
      }).then(response => {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let partialData = '';

        const processStream = ({done, value}: { done: boolean, value: Uint8Array }) => {
          const chunk = decoder.decode(value, {stream: true});
          partialData += chunk;
          const lines = partialData.split('\n');
          partialData = lines.pop() || '';
          lines.forEach(line => {
            if (line.startsWith('data: ')) {
              const dataString = line.slice(6);
              try {
                const jsonObject = JSON.parse(dataString);
                if (Array.isArray(jsonObject)) {
                  observer.next(jsonObject[0]);
                } else {
                  observer.next(jsonObject);
                }
              } catch (e) {
                observer.next({data: dataString[0]});
              }
            }
          });
          if (done) {
            observer.complete();
            return;
          }
          reader?.read().then(processStream);
        };

        reader?.read().then(processStream);
      }).catch(err => {
        observer.error(err);
      });
    });
  }

  public fetchPromptReport(data: FormData, isFileAnalysis: boolean = false): Observable<GenericResModel<CommonAIResModel>> {
    const uuid = this.generateUuid();
    this.currentUUID = uuid;
    this.progressType = isFileAnalysis ? 'FILE_PROMPT' : 'PROMPT';
    const url = environment.ai + 'ai-prompt';
    return this.httpClient.post<GenericResModel<CommonAIResModel>>(url, data).pipe(
      tap(res => this.cachePromptResponse(res, uuid))
    );
  }

  public fetchPromptReportForAFile(data: FormData): Observable<GenericResModel<CommonAIResModel>> {
    const uuid = this.generateUuid();
    this.currentUUID = uuid;
    this.progressType = 'FILE_PROMPT';
    const url = environment.ai + 'ai-prompt-with-file';
    return this.httpClient.post<GenericResModel<CommonAIResModel>>(url, data).pipe(
      tap(res => this.cachePromptResponse(res, uuid))
    );
  }

  public fetchReconciliation(data: FormData): Observable<GenericResModel<CommonAIResModel>> {
    const uuid = this.generateUuid();
    this.currentUUID = uuid;
    this.progressType = 'FILE_PROMPT';
    const url = environment.ai + 'bank-reconciliation';
    return this.httpClient.post<GenericResModel<CommonAIResModel>>(url, data).pipe(
      tap(res => this.cachePromptResponse(res, uuid))
    );
  }

  public fetchAiFirmSettings(): Observable<GenericResModel<AiFirmModel>> {
    const url = environment.apiV2 + '/firm/luca';
    return this.httpClient.get<GenericResModel<AiFirmModel>>(url);
  }

  public updateAiFirmStatus(data: AiFirmModel): Observable<GenericResModel<AiFirmModel>> {
    const url = environment.apiV1 + '/luca/firm-settings';
    return this.httpClient.post<GenericResModel<AiFirmModel>>(url, data);
  }

  public fetchAIInformation(): Observable<GenericResModel<AiFirmInfo>> {
    const url = environment.apiV1 + '/luca/setup-count';
    return this.httpClient.get<GenericResModel<AiFirmInfo>>(url);
  }

  public fetchAiUserStatus(): Observable<GenericResModel<AiInfoModel>> {
    const url = environment.apiV1 + '/luca/status';
    return this.httpClient.get<GenericResModel<AiInfoModel>>(url);
  }

  public updateAiUserStatus(): Observable<GenericResModel<boolean>> {
    const url = environment.apiV1 + '/luca/status';
    return this.httpClient.post<GenericResModel<boolean>>(url, {});
  }

  public updateDeactivation(param: string = 'deactivation_notification'): Observable<GenericResModel<boolean>> {
    const url = environment.apiV1 + `/luca/status?action=${param}`;
    return this.httpClient.put<GenericResModel<boolean>>(url, {});
  }

  public fetchChatHistory(engagementId: number = 0): Observable<GenericResModel<ChatsModel[]>> {
    const url: string = environment.apiV1 + `/luca/chats?engagementId=${engagementId}`;
    return this.httpClient.get<GenericResModel<ChatsModel[]>>(url);
  }

  public createOrUpdateChat(chat: ChatsModel): Observable<GenericResModel<ChatsModel>> {
    const url: string = environment.apiV1 + '/luca/chats';
    return this.httpClient.post<GenericResModel<ChatsModel>>(url, chat);
  }

  public downloadAiResponse(data: AiDownloadModel) {
    const url = environment.apiV1 + '/luca/download-report';
    return this.httpClient.post(url, data, {responseType: 'blob'});
  }

  public saveFeedback(data: FormData): Observable<GenericResModel<boolean>> {
    const url = environment.apiV1 + '/luca/save-feedback';
    return this.httpClient.post<GenericResModel<boolean>>(url, data);
  }

  public deleteChat(chat: ChatsModel): Observable<GenericResModel<boolean>> {
    const url = environment.apiV1 + `/luca/delete/chat`;
    return this.httpClient.post<GenericResModel<boolean>>(url, chat);
  }

  public downloadSystemFile(data: AiDownloadModel) {
    const url = environment.apiV1 + '/luca/download-file';
    return this.httpClient.post(url, data, {responseType: 'blob'});
  }

  public saveUserSelectedInputs(data: SaveChatMsgModel): Observable<GenericResModel<SavedChatHistoryModel>> {
    const url = environment.apiV1 + '/luca/chat-messages';
    return this.httpClient.put<GenericResModel<SavedChatHistoryModel>>(url, data);
  }

  public saveToEngagement(data: AiDownloadModel): Observable<GenericResModel<any>> {
    const url = environment.apiV1 + '/luca/save-eng';
    return this.httpClient.post<GenericResModel<any>>(url, data);
  }

  public convertToExcel(data: SaveExcelModel): Observable<GenericResModel<CommonAIResModel>> {
    const url = environment.ai + 'convert_excel/';
    return this.httpClient.post<GenericResModel<CommonAIResModel>>(url, data);
  }

  public getFormattedCreatedAt(createdDate: any, title: string): string {
    const now = new Date();
    const createdAtDate = new Date(createdDate);
    if (this.isCheckSameDay(now, createdAtDate)) {
      return `${title} (Today, ${this.countableTimeZonePipe.transform(createdDate, 'HH:mm a', null)})`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (this.isCheckSameDay(yesterday, createdAtDate)) {
      return `${title} (Yesterday, ${this.countableTimeZonePipe.transform(createdDate, 'HH:mm a', null)})`;
    }
    return `${title} (${this.countableTimeZonePipe.transform(createdDate, 'dd MMM yyyy, hh:mm a', null)})`;
  }

  public isCheckSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  public getContextDetails(engagementId: number, sourceId: number, accountYear: string): Observable<GenericResModel<ContextResModel>> {
    const url: string = environment.apiV1 + `/luca/context/tb?engagementId=${engagementId}&sourceId=${sourceId}&accountYear=${accountYear}`;
    return this.httpClient.get<GenericResModel<ContextResModel>>(url);
  }

  public fetchAutoDocRepoByClient(clientFirmId: number): Observable<GenericResModel<AutoDocDataModel>> {
    const url: string = environment.apiV1 + `/doc-repo?clientFirmId=${clientFirmId}`;
    return this.httpClient.get<GenericResModel<AutoDocDataModel>>(url);
  }

  public fetchTbAccounts(engagementId: number, accountYear: number): Observable<GenericResModel<TbAccountModel[]>> {
    const url = environment.apiV1 + `/tb/accounts/${engagementId}/${accountYear}`;
    return this.httpClient.get<GenericResModel<TbAccountModel[]>>(url);
  }

  public updateOtherApiForContext(input: SaveOtherApiModel): Observable<GenericResModel<ContextOtherApiRes>> {
    const url = environment.ai + `context/other-api`;
    return this.httpClient.post<GenericResModel<ContextOtherApiRes>>(url, input);
  }

  private cachePromptResponse(response: GenericResModel<CommonAIResModel>, uuid: string) {
    this.cachedChatResponse.clear();
    this.cachedPromptResponse.clear();
    if (uuid === this.currentUUID && this.isMinimized) {
      this.cachedPromptResponse.clear();
      this.cachedPromptResponse.set(uuid, response);
    }
  }

  public generateUuid(): string {
    this.resetValues();
    const timestamp = Date.now();
    const randomSegment = Math.floor(Math.random() * 1000000);
    return `${timestamp}-${randomSegment}`;
  }

  public resetValues() {
    this.currentUUID = null;
    this.cachedChatResponse.clear();
    this.cachedPromptResponse.clear();
    this.isMinimized = false;
  }
}
